import type { StudioResponse } from "../lib/api.ts";
import { STUDIO } from "../lib/studio.ts";

export function Faq({ studio }: { studio: StudioResponse | null }) {
  const deposit = studio?.depositPercent ?? STUDIO.depositPercentFallback;
  const cancellationHours = studio?.cancellationWindowHours ?? STUDIO.cancellationHoursFallback;
  const questions = [
    {
      question: "What happens at my first appointment?",
      paragraphs: ["Your first appointment begins with a short chat about your consultation form to discuss your skin, concerns and goals. I’ll then tailor your facial to your needs and provide aftercare advice afterwards."],
    },
    {
      question: "Do I need to complete a consultation form?",
      paragraphs: ["Yes. All new clients are asked to complete a consultation form ahead of their appointment. This helps me to prepare for your treatment and understand your skin, medical history and any potential contraindications, ensuring your treatment is both safe and suitable for you."],
    },
    {
      question: "Do I need to do anything before a facial?",
      paragraphs: ["There's very little preparation needed. Where possible, avoid using strong exfoliating products or active skincare ingredients immediately before your appointment. Please also let me know about any recent aesthetic treatments, changes to medication or skin concerns."],
    },
    {
      question: "Can I wear make-up to my appointment?",
      paragraphs: ["Of course! Your treatment will include a thorough cleanse to remove makeup, SPF and impurities, so there's no need to arrive with a bare face."],
    },
    {
      question: "What skincare products do you use?",
      paragraphs: ["At The Renewal Studio, we use professional Dermalogica skincare products, selected to suit your skin type, concerns and goals. Dermalogica combines advanced skincare science with professional treatments to help achieve healthier, more radiant skin."],
    },
    {
      question: "Will I need to follow any aftercare advice?",
      paragraphs: ["Yes. Following your treatment, I'll provide simple aftercare guidance to help you get the most from your facial. This may include avoiding makeup, heat, strenuous exercise and certain active skincare ingredients for a short period, depending on your treatment."],
    },
    {
      question: "What if I’m unwell before my appointment?",
      paragraphs: [
        "If you have a cough or a fever, please contact me as we may need to postpone your treatment.",
        "I will also apply these standards to myself. If I feel unwell, unable to treat or it is not advisable for me to treat you, I will explain the situation to you as soon as possible before your treatment.",
      ],
    },
    {
      question: "What is your cancellation policy?",
      paragraphs: [
        `A ${deposit}% deposit is required upon booking and will be charged if cancellations are made with less than ${cancellationHours} hours notice.`,
        "As a small independent business, no-shows and last-minute cancellations can have a significant impact. I sincerely appreciate your understanding and consideration for my time and other clients awaiting appointments.",
      ],
    },
    {
      question: "Do you treat men?",
      paragraphs: ["As a touch therapist I choose to only treat females unless a male is referred by a current client."],
    },
    {
      question: "Is there parking available at The Renewal Studio?",
      paragraphs: ["Yes, there is plenty of off-street parking available just outside."],
    },
    {
      question: "Is The Renewal Studio accessible?",
      paragraphs: ["The Renewal Studio is located at the top of the stairs of my home. Please get in touch before booking if you have any accessibility concerns."],
    },
  ];

  return (
    <section className="section tone-cream" id="faq" aria-labelledby="faq-title">
      <div className="page faq">
        <div className="faq__intro">
          <p className="eyebrow">Before your visit</p>
          <h2 id="faq-title">Frequently asked questions</h2>
          <p className="faq__lede">A few helpful details before your appointment.</p>
        </div>
        <div className="faq__questions">
          {questions.map(({ question, paragraphs }) => (
            <details className="faq__item" key={question}>
              <summary>{question}<span className="faq__icon" aria-hidden="true" /></summary>
              <div className="faq__answer">
                {paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
